import React, { useState, useEffect } from 'react';
import {
  CaretDown, CaretUp, Heartbeat, Mountains, LinkSimpleBreak, ChartBar, SmileySad,
} from '@phosphor-icons/react';
import { CYCLING_ENABLED, GARMIN_ENABLED } from '../config/features';
import { fetchGarminActivities, fetchGarminLaps, fetchGarminSplits, fetchGarminStatus } from '../utils/garmin';
import { garminKmRows, garminLapRows } from '../utils/splitRows';
import SplitsTable from './SplitsTable';
import { Header, Skeleton, EmptyState } from './ui';
import WeeklyVolumeChart, { truncKm } from './WeeklyVolumeChart';
import authFetch from '../utils/authFetch';
import GarminLogo from './brand/GarminLogo';
import { A2Banner } from './home2/HomeA2';

// Garmin: server cap 100 (Strava removal C2, 2026-10-21: Garmin is the only activity source).
const ACTIVITY_LIMIT = 100;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const localIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Common auto-generated English activity names, translated (handoff_app_design README §5.4). A
// custom name the athlete typed themselves is kept exactly as-is — only these defaults are
// translated, so nothing the user wrote is ever rewritten.
const _DEFAULT_ACTIVITY_NAMES = {
  'morning run': 'ריצת בוקר',
  'lunch run': 'ריצת צהריים',
  'afternoon run': 'ריצת אחר הצהריים',
  'evening run': 'ריצת ערב',
  'night run': 'ריצת לילה',
  'morning ride': 'רכיבת בוקר',
  'afternoon ride': 'רכיבת אחר הצהריים',
  'evening ride': 'רכיבת ערב',
  'run': 'ריצה',
  'ride': 'רכיבה',
};
const HEB_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const HEB_WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

function ActivityList({ onBack, userId }) {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [activitySplits, setActivitySplits] = useState({});
  const [loadingSplits, setLoadingSplits] = useState(false);
  // Garmin panel view per activity: 'laps' (watch laps / workout steps) | 'km' (per-km splits).
  const [splitsView, setSplitsView] = useState({});
  // Garmin (via Stridee, pilot): false when the pilot is off / the user is not in it / not connected.
  const [garminConnected, setGarminConnected] = useState(GARMIN_ENABLED ? null : false);
  // The current week's planned km from the stored plan (the chart's dashed outline); null = no plan / not read.
  const [plannedKm, setPlannedKm] = useState(null);

  // Chart period — how many weeks back the summary + bars cover.
  const [chartPeriod, setChartPeriod] = useState('8w');
  const periodOptions = [
    { value: '4w',  label: '4 שבועות',  weeks: 4 },
    { value: '8w',  label: '8 שבועות',  weeks: 8 },
    { value: '12w', label: '12 שבועות', weeks: 12 },
    { value: '16w', label: '16 שבועות', weeks: 16 },
    { value: '6m',  label: '6 חודשים',  weeks: 26 },
    { value: '12m', label: 'שנה',       weeks: 52 },
  ];
  const periodConfig = periodOptions.find(p => p.value === chartPeriod) || periodOptions[1];

  // ── Type detection (kept from previous version; dormant while cycling is hidden) ──
  const isCycling = (activity) => {
    const type = activity.activity_type || activity.activityType || activity.sport_type || 'Run';
    return type.toLowerCase().includes('ride') || type.toLowerCase().includes('cycling');
  };
  const isRunning = (activity) => {
    const type = activity.activity_type || activity.activityType || activity.sport_type || 'Run';
    return type.toLowerCase().includes('run');
  };

  // Cycling is hidden from the UI entirely while CYCLING_ENABLED is false (§5.4: "remove the
  // sport tabs while cycling is disabled") — the filter itself is kept so nothing breaks the day
  // this flips back on.
  const filteredActivities = activities.filter(a => CYCLING_ENABLED || !isCycling(a));

  // ── Format helpers ────────────────────────────────────────
  // A value the activity does not carry returns null and its slot is hidden — never "--".
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return null;
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };
  const calculatePace = (distanceKm, durationSeconds) => {
    if (!distanceKm || !durationSeconds || distanceKm === 0) return null;
    const paceSeconds = durationSeconds / distanceKm;
    return `${Math.floor(paceSeconds / 60)}:${Math.floor(paceSeconds % 60).toString().padStart(2, '0')}`;
  };
  const calculateSpeed = (distanceKm, durationSeconds) => {
    if (!distanceKm || !durationSeconds || durationSeconds === 0) return null;
    return ((distanceKm / durationSeconds) * 3600).toFixed(1);
  };
  const getActivityDate = (activity) =>
    activity.date || (activity.startTimeLocal ? activity.startTimeLocal.split('T')[0] : null);

  // Translate the default English names; anything else (a name the athlete typed) is untouched.
  const displayName = (activity) => {
    const raw = (activity.activityName || activity.name || '').trim();
    if (!raw) return isCycling(activity) ? 'רכיבה' : 'ריצה';
    const heb = _DEFAULT_ACTIVITY_NAMES[raw.toLowerCase()];
    return heb || raw;
  };

  // "חמישי, 24 בספטמבר · 10:30" — weekday + day + Hebrew month, and the time when available.
  const formatFullDate = (dateStr, timeStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    if (Number.isNaN(dt.getTime())) return dateStr;
    const wd = HEB_WEEKDAYS[dt.getDay()];
    const datePart = `${wd}, ${dt.getDate()} ב${HEB_MONTHS[dt.getMonth()]}`;
    let timePart = '';
    if (timeStr) {
      try {
        const td = new Date(timeStr);
        if (!Number.isNaN(td.getTime())) {
          timePart = ` · ${td.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
        }
      } catch { /* no time part */ }
    }
    return datePart + timePart;
  };

  // ── Weekly bucket helper for the chart (kept from previous version) ─────────────
  const buildWeeklyData = (acts, weeks) => {
    const buckets = new Map();
    const today = new Date();
    for (let w = weeks - 1; w >= 0; w--) {
      const ref = new Date(today);
      ref.setDate(today.getDate() - w * 7);
      const sunday = new Date(ref);
      sunday.setDate(ref.getDate() - ref.getDay());
      sunday.setHours(0, 0, 0, 0);
      const key = sunday.toISOString().slice(0, 10);
      buckets.set(key, { startDate: key, start: new Date(sunday), km: 0, runs: 0 });
    }
    acts.forEach(a => {
      const dateStr = getActivityDate(a);
      if (!dateStr) return;
      const aDate = new Date(dateStr);
      if (Number.isNaN(aDate.getTime())) return;
      const sunday = new Date(aDate);
      sunday.setDate(aDate.getDate() - aDate.getDay());
      sunday.setHours(0, 0, 0, 0);
      const key = sunday.toISOString().slice(0, 10);
      const bucket = buckets.get(key);
      if (bucket) { bucket.km += Number(a.distance || 0); bucket.runs += 1; }
    });
    return Array.from(buckets.values());
  };
  const weeklyData = buildWeeklyData(filteredActivities, periodConfig.weeks);
  // Period summary over the same weeks the bars show (truncated, never rounded): total km, run count, and the
  // average per week WITH running — a week without any run does not drag the average down.
  const periodSummary = (() => {
    const total = weeklyData.reduce((a, w) => a + w.km, 0);
    const runs = weeklyData.reduce((a, w) => a + w.runs, 0);
    const active = weeklyData.filter((w) => w.km > 0).length;
    return { km: truncKm(total, 1), count: runs, avg: active ? truncKm(total / active, 1) : null };
  })();

  // ── Month grouping (§5.4: "list grouped by month") — the Garmin list is sorted
  // newest-first in fetchActivities, so groups are built in encounter order without re-sorting. ──
  const monthGroups = (() => {
    const groups = [];
    const byKey = new Map();
    filteredActivities.forEach(a => {
      const dateStr = getActivityDate(a);
      if (!dateStr) return;
      const [y, m] = dateStr.split('-').map(Number);
      const key = `${y}-${m}`;
      if (!byKey.has(key)) {
        const g = { key, label: `${HEB_MONTHS[(m || 1) - 1]} ${y}`, items: [] };
        byKey.set(key, g);
        groups.push(g);
      }
      byKey.get(key).items.push(a);
    });
    return groups;
  })();

  // ── Splits expansion (kept from previous version; Garmin rows read their own splits) ──────────
  const toggleSplits = async (activity) => {
    const activityId = activity.activityId;
    if (expandedActivity === activityId) { setExpandedActivity(null); return; }
    setExpandedActivity(activityId);
    if (activitySplits[activityId]) return;
    setLoadingSplits(true);
    try {
      if (activity.source === 'garmin') {
        // Both views are read up front so the toggle is instant. A structured watch workout opens on its
        // laps (work / rest steps); an ordinary run keeps the per-km view as the default.
        const [lapsView, splits] = await Promise.all([
          fetchGarminLaps(activity.garmin_id).catch(() => null),
          fetchGarminSplits(activity.garmin_id).catch(() => []),
        ]);
        const laps = garminLapRows(lapsView);
        const km = garminKmRows(splits);
        setActivitySplits(prev => ({ ...prev, [activityId]: { garmin: true, laps, km } }));
        setSplitsView(prev => ({ ...prev, [activityId]: (lapsView && lapsView.structured && laps.length) || !km.length ? 'laps' : 'km' }));
      }
    } catch (err) {
      console.error('Error fetching splits:', err);
    } finally {
      setLoadingSplits(false);
    }
  };

  const checkGarminStatus = async () => {
    if (!GARMIN_ENABLED || !userId) { setGarminConnected(false); return; }
    try {
      const s = await fetchGarminStatus();
      setGarminConnected(!!(s && s.eligible && s.status === 'connected'));
    } catch {
      setGarminConnected(null);   // status unknown (request failed) — never shown as "not connected"
    }
  };

  useEffect(() => {
    checkGarminStatus();
    fetchActivities();
    fetchPlannedKm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // The current week's planned km — read from the stored plan (same endpoint the Home and Plan screens read). Any
  // failure leaves it null: the chart then simply has no planned outline.
  const fetchPlannedKm = async () => {
    if (!userId) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/weekly-plan/all-active?user_id=${encodeURIComponent(userId)}`);
      if (!res || !res.ok) return;
      const data = await res.json();
      const sunday = new Date(); sunday.setDate(sunday.getDate() - sunday.getDay());
      const key = localIso(sunday);
      const row = (Array.isArray(data && data.plans) ? data.plans : []).find((p) =>
        (p.sport_type || 'running') === 'running' && String(p.week_start_date || '').slice(0, 10) === key);
      const wj = row && row.workouts_json && typeof row.workouts_json === 'object' ? row.workouts_json : null;
      if (!wj) return;
      const km = wj.total_km > 0 ? wj.total_km
        : (Array.isArray(wj.days) ? wj.days.reduce((a, d) => a + (d.is_rest ? 0 : Number(d.km || 0)), 0) : 0);
      setPlannedKm(km > 0 ? km : null);
    } catch { /* no plan outline */ }
  };

  // Newest first — month grouping below relies on this order.
  const sortKey = (a) => String(a.startTimeLocal || a.date || '');

  const fetchActivities = async () => {
    if (!userId) { setError('לא מחובר למערכת'); setIsLoading(false); return; }
    try {
      setIsLoading(true);
      setError(null);
      const garmin = GARMIN_ENABLED ? await fetchGarminActivities(ACTIVITY_LIMIT) : [];
      setActivities((Array.isArray(garmin) ? garmin : []).slice().sort((x, y) => sortKey(y).localeCompare(sortKey(x))));
    } catch (err) {
      setError('לא הצלחתי לטעון את האימונים');
    } finally {
      setIsLoading(false);
    }
  };


  // ────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-bg">
    <div className="max-w-lg mx-auto" dir="rtl">

      <Header
        title="אימונים"
        subtitle={garminConnected ? 'מסונכרן מ-Garmin' : undefined}
        onBack={onBack}
      />

      <main className="px-4 py-6">
        <div className="max-w-lg mx-auto flex flex-col gap-4">

          {garminConnected === false && !isLoading && (
            <A2Banner
              Icon={LinkSimpleBreak}
              accent="accent"
              title="אין מקור אימונים מחובר"
              body="חבר Garmin מהפרופיל כדי לראות אימונים"
              actions={[{ label: 'לפרופיל', onClick: onBack }]}
            />
          )}

          {isLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton rounded="card" className="h-32" />
              <Skeleton rounded="card" className="h-44" />
            </div>
          )}

          {error && !isLoading && (
            <EmptyState
              icon={<SmileySad weight="bold" />}
              message={error}
              action={{ label: 'נסה שוב', onClick: fetchActivities }}
            />
          )}

          {!isLoading && !error && activities.length === 0 && garminConnected !== false && (
            <EmptyState
              icon={<ChartBar weight="bold" />}
              message="אין פעילויות עדיין. האימונים יופיעו כאן אחרי סנכרון מ-Garmin"
            />
          )}

          {!isLoading && !error && filteredActivities.length > 0 && (
            <>
              {/* ── Weekly volume: period total + average, then the bars (WeeklyVolumeChart) ── */}
              <section className="rounded-[16px] bg-surface border border-border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0" data-testid="period-summary">
                    <div className="flex items-baseline gap-1.5">
                      <span className="num-display text-text" style={{ fontSize: 56, lineHeight: 0.9 }} data-testid="period-total">
                        {periodSummary.km}
                      </span>
                      <span className="text-[14px] text-text-muted">ק״מ</span>
                    </div>
                    <div className="text-[13px] text-text-muted mt-1.5">
                      {periodSummary.avg != null && <>ממוצע <span className="text-text font-bold tabular-nums">{periodSummary.avg}</span> ק״מ לשבוע · </>}
                      {periodSummary.count} ריצות
                    </div>
                  </div>
                  <div className="relative inline-flex items-center shrink-0">
                    <select
                      value={chartPeriod}
                      onChange={(e) => setChartPeriod(e.target.value)}
                      aria-label="תקופה"
                      className="appearance-none h-11 ps-8 pe-3 rounded-full bg-transparent border border-border text-text-body font-semibold text-[13px] cursor-pointer"
                    >
                      {periodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <CaretDown size={14} weight="bold" className="absolute start-3 text-text-muted pointer-events-none" />
                  </div>
                </div>

                <div className="mt-5">
                  <WeeklyVolumeChart weeks={weeklyData} plannedKm={plannedKm} periodWeeks={periodConfig.weeks} />
                </div>
              </section>

              {/* ── Activity list, grouped by month (§5.4) ── */}
              {monthGroups.map(group => (
                <div key={group.key} className="flex flex-col gap-2.5">
                  <span className="font-semibold text-[13px] text-text-muted">{group.label}</span>
                  <div className="flex flex-col gap-2.5">
                    {group.items.map((activity, idx) => {
                      const isRun = isRunning(activity);
                      const distanceKm = Number(activity.distance || 0);
                      const durationSec = Number(activity.duration || 0);
                      const isCycle = isCycling(activity);
                      const splitsForActivity = activitySplits[activity.activityId];
                      const isOpen = expandedActivity === activity.activityId;
                      // "Garmin [device model]" (Garmin API Brand Guidelines). The model is the backend's device_model —
                      // Stridee's value, brand prefix removed; none -> "Garmin" alone. Never derived or guessed here.
                      const deviceModel = typeof activity.device_model === 'string' ? activity.device_model.trim() : '';

                      const pace = isCycle
                        ? (activity.averageSpeed ? Number(activity.averageSpeed).toFixed(1) : calculateSpeed(distanceKm, durationSec))
                        : (activity.averagePace || activity.pace || calculatePace(distanceKm, durationSec));
                      const duration = formatDuration(durationSec);
                      const metrics = [
                        distanceKm > 0 && { v: distanceKm.toFixed(2), l: 'ק״מ' },
                        pace && { v: pace, l: isCycle ? 'קמ״ש' : 'לק״מ' },
                        duration && { v: duration, l: 'זמן' },
                      ].filter(Boolean);

                      return (
                        <div
                          key={activity.activityId || idx}
                          className="rounded-[16px] bg-surface border border-border p-4"
                        >
                          <div className="flex items-center justify-between gap-2.5">
                            <div className="min-w-0">
                              <div className="font-bold text-[16px] text-text truncate">{displayName(activity)}</div>
                              <div className="font-medium text-[13px] text-text-muted mt-0.5">
                                {formatFullDate(getActivityDate(activity), activity.startTimeLocal)}{' '}
                                {/* separator + "Garmin [model]" wrap as ONE unit (a narrow screen moves both to the next line) */}
                                <span data-testid="activity-source" className="inline-block max-w-full">· <bdi dir="ltr"><GarminLogo variant="inline" size={12} />{deviceModel ? <span data-testid="activity-device"> {deviceModel}</span> : null}</bdi></span>
                              </div>
                            </div>
                          </div>

                          {metrics.length > 0 && (
                            <div className="grid grid-cols-3 mt-3">
                              {metrics.map((m, mi) => (
                                <div key={m.l} className={`min-w-0 px-3 ${mi ? 'border-s border-border' : 'ps-0'}`}>
                                  <div className="num-display text-[30px] text-text tabular-nums" style={{ lineHeight: 1 }}>{m.v}</div>
                                  <div className="text-[12px] text-text-muted mt-1">{m.l}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {(() => {
                            const hr = activity.averageHR;
                            const elev = activity.elevation_gain || activity.elevationGain;
                            if (!hr && !elev) return null;
                            return (
                              <div className="flex items-center gap-1 font-medium text-[13px] text-text-muted mt-2.5">
                                {hr > 0 && <span className="inline-flex items-center gap-1"><Heartbeat size={13} weight="bold" />{Math.round(hr)}</span>}
                                {hr > 0 && elev > 0 && <span>·</span>}
                                {elev > 0 && <span className="inline-flex items-center gap-1"><Mountains size={13} weight="bold" />{Math.round(elev)} מ׳</span>}
                              </div>
                            );
                          })()}

                          {isRun && (
                            <div className="flex items-center justify-end mt-3">
                              <button
                                type="button"
                                aria-expanded={isOpen}
                                onClick={() => toggleSplits(activity)}
                                className="h-11 px-4 inline-flex items-center gap-1.5 rounded-full border border-border text-text-body text-[13px] font-semibold"
                              >
                                {isOpen ? 'הסתר ספליטים' : 'הצג ספליטים'}
                                {isOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                              </button>
                            </div>
                          )}

                          {isRun && isOpen && (
                            <div className="mt-3 rounded-[12px] bg-bg border border-border overflow-hidden">
                              {loadingSplits && !splitsForActivity && (
                                <p className="text-center text-text-muted text-xs py-3">טוען ספליטים…</p>
                              )}
                              {splitsForActivity && (() => {
                                const { garmin, laps, km = [] } = splitsForActivity;
                                const view = garmin ? (splitsView[activity.activityId] || 'km') : 'laps';
                                const rows = view === 'laps' ? laps : km;
                                if (!laps.length && !km.length) {
                                  return <p className="text-center text-text-muted text-xs py-3">אין ספליטים לאימון הזה.</p>;
                                }
                                return (
                                  <>
                                    {garmin && laps.length > 0 && km.length > 0 && (
                                      <div role="tablist" className="flex gap-1 p-1.5 border-b border-divider">
                                        {[['laps', 'קטעי אימון'], ['km', 'לפי ק״מ']].map(([key, label]) => (
                                          <button
                                            key={key} type="button" role="tab" aria-selected={view === key}
                                            onClick={() => setSplitsView(prev => ({ ...prev, [activity.activityId]: key }))}
                                            className="flex-1 h-8 rounded-control text-[13px] font-semibold"
                                            style={view === key
                                              ? { background: 'var(--accent)', color: 'var(--on-accent)' }
                                              : { background: 'transparent', color: 'var(--text-muted)' }}
                                          >{label}</button>
                                        ))}
                                      </div>
                                    )}
                                    <SplitsTable
                                      rows={rows}
                                      firstLabel={view === 'km' ? 'ק״מ' : 'קטע'}
                                      footnote={view === 'km' ? 'הקילומטר המהיר ביותר — העמודה הגבוהה, מודגש.' : 'הקטע המהיר ביותר — העמודה הגבוהה, מודגש.'}
                                    />
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}

        </div>
      </main>
    </div>
    </div>
  );
}

export default ActivityList;
